import { useDisclosure } from "@heroui/react";
import { InputField, ModalBuilder, ModalError } from "../modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { AccountingIntegration } from "@/api/accounting/integrations";

interface IEditAccountingIntegrationForm {
  displayName: string;
  instanceUrl: string;
  username: string;
  password: string;
  enabled: boolean;
  businessRequisiteId: number;
}

const editAccountingIntegrationFields: InputField[] = [
  {
    label: "Название",
    name: "displayName",
    placeholder: "Моя интеграция",
    type: "text",
    isRequired: true,
  },
  {
    label: "URL инстанса",
    name: "instanceUrl",
    placeholder: "https://example.com",
    type: "text",
    isRequired: true,
  },
  {
    label: "Пользователь",
    name: "username",
    placeholder: "admin",
    type: "text",
    isRequired: true,
  },
  {
    label: "Пароль",
    name: "password",
    placeholder: "••••••••",
    type: "password",
  },
  {
    label: "Активна",
    name: "enabled",
    placeholder: "",
    type: "boolean",
  },
  {
    label: "Реквизиты",
    name: "businessRequisiteId",
    placeholder: "",
    type: "requisites",
    isRequired: true,
  },
];

export function EditAccountingIntegrationModal({
  disclosure,
  integration,
  onUpdated,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  integration: AccountingIntegration | null;
  onUpdated?: () => void;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);

  const handleError = useErrorHandling();

  const onSubmit = (data: IEditAccountingIntegrationForm) => {
    return new Promise<boolean>(async (resolve) => {
      if (!integration) {
        resolve(false);
        return;
      }

      api.accountingIntegrations
        .update(integration.id, {
          displayName: data.displayName,
          instanceUrl: data.instanceUrl,
          username: data.username,
          ...(data.password ? { password: data.password } : {}),
          enabled: data.enabled ?? false,
          businessRequisiteId: Number(data.businessRequisiteId),
        })
        .then(() => {
          toast.success("Интеграция успешно обновлена");
          disclosure.onClose();
          onUpdated?.();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErrors(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>(async (resolve) => {
      if (!integration) {
        resolve(false);
        return;
      }

      api.accountingIntegrations
        .delete(integration.id)
        .then(() => {
          toast.success("Интеграция успешно удалена");
          disclosure.onClose();
          onUpdated?.();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <ModalBuilder
      title="Редактирование интеграции"
      modalErrors={modalErrors}
      fields={editAccountingIntegrationFields}
      defaultValues={
        integration
          ? {
              displayName: integration.displayName,
              instanceUrl: integration.instanceUrl,
              username: integration.username,
              enabled: integration.enabled,
              businessRequisiteId: String(integration.businessRequisiteId),
            }
          : undefined
      }
      isOpen={isOpen}
      isSensitiveData={false}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onDelete={onDelete}
      submitButtonText="Сохранить"
      deleteButtonText="Удалить"
    />
  );
}
